import { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, User, LogOut, Loader2, Settings, Plus, Trash2, Edit2, X, Check, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/api";

const AUTH_TOKEN_KEY = "pole_dashboard_token";
const AUTH_USER_KEY = "pole_dashboard_user";

// Admin Users Management Dialog
function AdminUsersDialog() {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Create form state
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [creating, setCreating] = useState(false);
  
  // Edit form state
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      await axios.post(`${API}/admin/users`, {
        username: newUsername,
        password: newPassword,
        display_name: newDisplayName || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User '${newUsername}' created`);
      setNewUsername("");
      setNewPassword("");
      setNewDisplayName("");
      setShowCreateForm(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditDisplayName(user.display_name || "");
    setEditPassword("");
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      await axios.put(`${API}/admin/users/${editingUser.id}`, {
        display_name: editDisplayName || undefined,
        password: editPassword || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User '${editingUser.username}' updated`);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user '${user.username}'? This cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      await axios.delete(`${API}/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User '${user.username}' deleted`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <>
      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setOpen(true); }}>
        <Users className="w-4 h-4 mr-2" />
        Manage Users
      </DropdownMenuItem>
      
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Admin Users</DialogTitle>
            <DialogDescription>Add, edit, or remove users who can access the dashboard.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* User List */}
            {loading ? (
              <div className="text-center py-4">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
              </div>
            ) : (
              <div className="space-y-2">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    {editingUser?.id === user.id ? (
                      <div className="flex-1 space-y-2">
                        <Input
                          value={editDisplayName}
                          onChange={(e) => setEditDisplayName(e.target.value)}
                          placeholder="Display name"
                          className="h-8"
                        />
                        <Input
                          type="password"
                          value={editPassword}
                          onChange={(e) => setEditPassword(e.target.value)}
                          placeholder="New password (leave blank to keep)"
                          className="h-8"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleSaveEdit} disabled={saving}>
                            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingUser(null)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="font-medium text-sm">{user.display_name}</div>
                          <div className="text-xs text-slate-500">@{user.username}</div>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(user)}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(user)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Create New User Form */}
            {showCreateForm ? (
              <form onSubmit={handleCreate} className="space-y-3 p-4 border border-slate-200 rounded-lg">
                <h4 className="font-medium text-sm">New User</h4>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="Username"
                    required
                    className="h-9"
                  />
                  <Input
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    placeholder="Display name (optional)"
                    className="h-9"
                  />
                </div>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Password"
                  required
                  className="h-9"
                />
                <div className="flex gap-2">
                  <Button type="submit" size="sm" disabled={creating}>
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                    Create User
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setShowCreateForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <Button variant="outline" className="w-full" onClick={() => setShowCreateForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add New User
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function PasswordGate({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Check if we have a stored token and verify it
    verifyStoredSession();
  }, []);

  const verifyStoredSession = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const storedUser = localStorage.getItem(AUTH_USER_KEY);
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000 // 15 second timeout
      });

      if (response.data.valid) {
        setIsAuthenticated(true);
        setUser(response.data.user || JSON.parse(storedUser || '{}'));
      } else {
        // Server explicitly said session is invalid - clear it
        console.log("Session invalid per server response:", response.data.message);
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_USER_KEY);
      }
    } catch (err) {
      // IMPORTANT: Don't clear session on network errors!
      // Only clear if server explicitly says session is invalid
      // Network errors (502, timeout, etc.) should preserve existing session
      console.error("Session verification network error (keeping session):", err.message);
      
      // If we have stored user data, assume session is still valid
      // User will naturally re-verify when they try authenticated actions
      if (storedUser) {
        try {
          setIsAuthenticated(true);
          setUser(JSON.parse(storedUser));
        } catch (parseErr) {
          // Only clear if we can't parse stored user AND have network issues
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(AUTH_USER_KEY);
        }
      }
      // If no stored user, just don't authenticate - let user log in fresh
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: username.trim(),
        password: password
      });

      if (response.data.success) {
        // Store token and user in localStorage (persists across browser sessions)
        localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.data.user));
        
        setIsAuthenticated(true);
        setUser(response.data.user);
        setError("");
      } else {
        setError(response.data.message || "Login failed");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Invalid username or password");
      } else {
        setError(err.response?.data?.detail || "Login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error("Logout error:", err);
    }

    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    setIsAuthenticated(false);
    setUser(null);
    setUsername("");
    setPassword("");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
          <p className="text-sm text-slate-500">Checking session...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div>
        {/* User info bar - Hidden on mobile (side nav has these options) */}
        <div className="hidden sm:flex fixed top-4 right-4 z-50 items-center gap-2">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 px-3 py-1.5 flex items-center gap-2">
            <User className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">{user?.display_name || user?.username || 'User'}</span>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500">
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <AdminUsersDialog />
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <CardTitle>Dashboard Login</CardTitle>
          <CardDescription>Sign in to access the pole rental dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  className="pl-10"
                  autoFocus
                  autoComplete="username"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className={`pl-10 pr-10 ${error ? "border-red-500" : ""}`}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {error && <p className="text-sm text-red-500">{error}</p>}
            
            <Button type="submit" className="w-full" disabled={submitting || !username || !password}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
          
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center">
              Sessions are saved for 6 months. You can be logged in on multiple devices.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Export helper to get auth token for API calls
export const getAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);
export const getAuthHeaders = () => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};
